import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Boxes,
  AlertTriangle,
  Plus,
  Save,
  RefreshCw,
  PackagePlus,
  History,
  Bell,
  ChevronDown,
  ChevronUp,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Settings2,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Overview Tab ==============
const OverviewTab = ({ inventory, loading, onRefresh, onUpdate, getAuthHeaders }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [editThreshold, setEditThreshold] = useState('');
  const [saving, setSaving] = useState(false);

  const handleStartEdit = (item) => {
    setEditingItem(item.sku);
    setEditQty(String(item.on_hand_qty));
    setEditThreshold(String(item.low_stock_threshold));
  };

  const handleSave = async (sku) => {
    const newQty = parseInt(editQty);
    const newThreshold = parseInt(editThreshold);
    if (isNaN(newQty) || newQty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (isNaN(newThreshold) || newThreshold < 0) {
      toast.error('Please enter a valid threshold');
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.patch(`${API}/admin/inventory/${sku}`, {
        on_hand_qty: newQty,
        low_stock_threshold: newThreshold
      }, { headers });
      setEditingItem(null);
      toast.success('Inventory updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update inventory');
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (item) => {
    if (item.on_hand_qty <= 0) return { label: 'Out of Stock', color: 'text-red-400 bg-red-500/15 border border-red-500/20' };
    if (item.on_hand_qty <= item.low_stock_threshold) return { label: 'Low Stock', color: 'text-amber-400 bg-amber-500/15 border border-amber-500/20' };
    return { label: 'In Stock', color: 'text-emerald-400 bg-emerald-500/15 border border-emerald-500/20' };
  };

  const totalOnHand = inventory.filter(i => !i.is_bundle).reduce((s, i) => s + i.on_hand_qty, 0);
  const lowStockCount = inventory.filter(i => !i.is_bundle && i.on_hand_qty <= i.low_stock_threshold).length;

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
          <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Total On Hand</p>
          <p className="text-2xl font-bold text-white" data-testid="total-on-hand">{totalOnHand.toLocaleString()}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
          <p className="text-[#888] text-xs uppercase tracking-wider mb-1">SKUs Tracked</p>
          <p className="text-2xl font-bold text-white">{inventory.filter(i => !i.is_bundle).length}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
          <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Low Stock</p>
          <p className={`text-2xl font-bold ${lowStockCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="low-stock-count">{lowStockCount}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
          <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Bundle Available</p>
          <p className="text-2xl font-bold text-white">{inventory.find(i => i.is_bundle)?.on_hand_qty || 0}</p>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead>
                <tr className="border-b border-[#2a2a2a]">
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">SKU</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Product</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Status</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">On Hand</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Reserved</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Available</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Alert At</th>
                  <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item) => {
                  const status = getStockStatus(item);
                  const isEditing = editingItem === item.sku;
                  return (
                    <tr key={item.sku} className="border-b border-[#2a2a2a] hover:bg-[#1e1e1e] transition-colors" data-testid={`inventory-row-${item.sku}`}>
                      <td className="p-4"><span className="font-mono text-white text-sm">{item.sku}</span></td>
                      <td className="p-4">
                        <span className="text-white">{item.product_name}</span>
                        {item.is_bundle && <span className="ml-2 text-[10px] text-[#888] bg-[#2a2a2a] px-1.5 py-0.5 rounded">BUNDLE</span>}
                      </td>
                      <td className="p-4"><span className={`text-xs px-2.5 py-1 rounded-full ${status.color}`}>{status.label}</span></td>
                      <td className="p-4">
                        {isEditing && !item.is_bundle ? (
                          <Input type="number" value={editQty} onChange={(e) => setEditQty(e.target.value)}
                            className="w-24 bg-[#111] border-[#3a3a3a] text-white text-center h-8" min="0" data-testid="edit-qty-input" />
                        ) : (
                          <span className={`text-lg font-semibold ${item.on_hand_qty <= item.low_stock_threshold ? 'text-amber-400' : 'text-white'}`}>{item.on_hand_qty}</span>
                        )}
                      </td>
                      <td className="p-4 text-[#666]">{item.reserved_qty || 0}</td>
                      <td className="p-4"><span className="text-white font-medium">{item.available_qty ?? item.on_hand_qty}</span></td>
                      <td className="p-4">
                        {isEditing && !item.is_bundle ? (
                          <Input type="number" value={editThreshold} onChange={(e) => setEditThreshold(e.target.value)}
                            className="w-20 bg-[#111] border-[#3a3a3a] text-white text-center h-8" min="0" data-testid="edit-threshold-input" />
                        ) : (
                          <span className="text-[#888]">{item.low_stock_threshold}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {item.is_bundle ? (
                          <span className="text-[#555] text-xs italic">Auto-calculated</span>
                        ) : isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <Button size="sm" onClick={() => handleSave(item.sku)} disabled={saving}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3" data-testid="save-edit-btn">
                              <Save className="w-3.5 h-3.5 mr-1" />Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}
                              className="text-[#888] h-8 px-2" data-testid="cancel-edit-btn">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleStartEdit(item)}
                            className="text-[#888] hover:text-white hover:bg-[#2a2a2a] h-8" data-testid={`edit-btn-${item.sku}`}>
                            <Settings2 className="w-3.5 h-3.5 mr-1.5" />Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== Add Batch Tab ==============
const AddBatchTab = ({ inventory, getAuthHeaders, onBatchAdded }) => {
  const nonBundleItems = inventory.filter(i => !i.is_bundle);
  const [batchName, setBatchName] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState(
    nonBundleItems.map(i => ({ sku: i.sku, product_name: i.product_name, qty_added: 0 }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [recentBatches, setRecentBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);

  const fetchBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/inventory/batches?limit=10`, { headers });
      setRecentBatches(res.data);
    } catch (e) {
      console.error('Failed to load batches', e);
    } finally {
      setLoadingBatches(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  useEffect(() => {
    if (nonBundleItems.length > 0 && items.length === 0) {
      setItems(nonBundleItems.map(i => ({ sku: i.sku, product_name: i.product_name, qty_added: 0 })));
    }
  }, [nonBundleItems]);

  const updateQty = (sku, value) => {
    setItems(prev => prev.map(i => i.sku === sku ? { ...i, qty_added: Math.max(0, parseInt(value) || 0) } : i));
  };

  const totalUnits = items.reduce((s, i) => s + i.qty_added, 0);

  const handleSubmit = async () => {
    if (!batchName.trim()) { toast.error('Please enter a batch name'); return; }
    const activeItems = items.filter(i => i.qty_added > 0);
    if (activeItems.length === 0) { toast.error('Add at least one item with quantity > 0'); return; }

    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/inventory/batches`, {
        batch_name: batchName.trim(),
        notes: notes.trim(),
        items: activeItems
      }, { headers });

      toast.success(`Batch "${batchName}" added — ${res.data.total_units} units`);
      setBatchName('');
      setNotes('');
      setItems(prev => prev.map(i => ({ ...i, qty_added: 0 })));
      fetchBatches();
      onBatchAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add batch');
    } finally {
      setSubmitting(false);
    }
  };

  const applyEvenSplit = (total) => {
    const perItem = Math.floor(total / nonBundleItems.length);
    setItems(prev => prev.map(i => ({ ...i, qty_added: perItem })));
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      {/* Add Batch Form */}
      <div className="xl:col-span-3">
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-[#D4540A]" />
            New Production Batch
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-[#888] mb-1.5">Batch Name *</label>
              <Input value={batchName} onChange={(e) => setBatchName(e.target.value)}
                placeholder="e.g., Production Run #1 — March 2026"
                className="bg-[#111] border-[#3a3a3a] text-white" data-testid="batch-name-input" />
            </div>

            <div>
              <label className="block text-sm text-[#888] mb-1.5">Notes</label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., 2500 total units, vendor XYZ"
                className="bg-[#111] border-[#3a3a3a] text-white" data-testid="batch-notes-input" />
            </div>

            {/* Quick Split */}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-[#888]">Quick fill:</span>
              {[600, 300, 150].map(n => (
                <Button key={n} variant="outline" size="sm"
                  className="border-[#3a3a3a] text-[#aaa] hover:text-white hover:bg-[#2a2a2a] h-7 text-xs"
                  onClick={() => applyEvenSplit(n * nonBundleItems.length)}
                  data-testid={`quick-fill-${n}`}>
                  {n}/each
                </Button>
              ))}
            </div>

            {/* Items */}
            <div className="space-y-3 pt-2">
              <p className="text-sm text-[#888]">Units to add per SKU:</p>
              {items.map((item) => (
                <div key={item.sku} className="flex items-center gap-3 bg-[#111] rounded-lg p-3 border border-[#222]">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{item.product_name}</p>
                    <p className="text-[#666] text-xs font-mono">{item.sku}</p>
                  </div>
                  <Input type="number" value={item.qty_added || ''} min="0"
                    onChange={(e) => updateQty(item.sku, e.target.value)}
                    placeholder="0"
                    className="w-24 bg-[#1a1a1a] border-[#3a3a3a] text-white text-center h-9"
                    data-testid={`batch-qty-${item.sku}`} />
                </div>
              ))}
            </div>

            {/* Total & Submit */}
            <div className="flex items-center justify-between pt-4 border-t border-[#2a2a2a]">
              <div>
                <span className="text-[#888] text-sm">Total units: </span>
                <span className="text-white font-bold text-lg" data-testid="batch-total-units">{totalUnits.toLocaleString()}</span>
              </div>
              <Button onClick={handleSubmit} disabled={submitting || totalUnits === 0}
                className="bg-[#D4540A] hover:bg-[#b8470a] text-white px-6" data-testid="submit-batch-btn">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Batch
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Batches */}
      <div className="xl:col-span-2">
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#D4540A]" />
            Recent Batches
          </h3>

          {loadingBatches ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : recentBatches.length === 0 ? (
            <p className="text-[#666] text-sm text-center py-8">No batches recorded yet</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {recentBatches.map((batch) => (
                <BatchCard key={batch.id} batch={batch} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BatchCard = ({ batch }) => {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(batch.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="bg-[#111] rounded-lg border border-[#222] overflow-hidden" data-testid={`batch-card-${batch.id}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-[#1a1a1a] transition-colors text-left">
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-medium truncate">{batch.batch_name}</p>
          <p className="text-[#666] text-xs">{date} &middot; {batch.total_units} units</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#888] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#888] shrink-0" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-[#222]">
          {batch.notes && <p className="text-[#888] text-xs mt-2 mb-2">{batch.notes}</p>}
          <div className="space-y-1.5 mt-2">
            {batch.items?.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-[#aaa] font-mono">{item.sku}</span>
                <span className="text-emerald-400 font-medium">+{item.qty_added}</span>
              </div>
            ))}
          </div>
          <p className="text-[#555] text-xs mt-2">By: {batch.created_by_email || 'Admin'}</p>
        </div>
      )}
    </div>
  );
};

// ============== History Tab ==============
const HistoryTab = ({ inventory, getAuthHeaders }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSku, setFilterSku] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      let url = `${API}/admin/inventory/history?limit=200`;
      if (filterSku) url += `&sku=${filterSku}`;
      if (filterType) url += `&event_type=${filterType}`;
      const res = await axios.get(url, { headers });
      setHistory(res.data);
    } catch (e) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterSku, filterType]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const eventTypeLabels = {
    batch_addition: { label: 'Batch Addition', color: 'text-emerald-400 bg-emerald-500/15' },
    order_deduction: { label: 'Order', color: 'text-red-400 bg-red-500/15' },
    manual_adjustment: { label: 'Manual Adjust', color: 'text-blue-400 bg-blue-500/15' },
    manual_set: { label: 'Manual Set', color: 'text-blue-400 bg-blue-500/15' },
    refund_restoration: { label: 'Refund', color: 'text-amber-400 bg-amber-500/15' },
    threshold_change: { label: 'Threshold', color: 'text-purple-400 bg-purple-500/15' },
  };

  const nonBundleItems = inventory.filter(i => !i.is_bundle);

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select value={filterSku} onChange={(e) => setFilterSku(e.target.value)}
          className="bg-[#1a1a1a] border border-[#3a3a3a] text-white rounded-lg px-3 py-2 text-sm"
          data-testid="history-filter-sku">
          <option value="">All SKUs</option>
          {nonBundleItems.map(i => <option key={i.sku} value={i.sku}>{i.sku} — {i.product_name}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="bg-[#1a1a1a] border border-[#3a3a3a] text-white rounded-lg px-3 py-2 text-sm"
          data-testid="history-filter-type">
          <option value="">All Events</option>
          <option value="batch_addition">Batch Addition</option>
          <option value="order_deduction">Order Deduction</option>
          <option value="manual_adjustment">Manual Adjustment</option>
          <option value="manual_set">Manual Set</option>
          <option value="refund_restoration">Refund Restoration</option>
          <option value="threshold_change">Threshold Change</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchHistory}
          className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* History Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-[#666]">
            <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No history entries yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="history-table">
              <thead>
                <tr className="border-b border-[#2a2a2a]">
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">SKU</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">Event</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">Change</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">Before</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">After</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">Reason</th>
                  <th className="text-left p-3 text-[#888] font-medium text-xs uppercase tracking-wider">By</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => {
                  const et = eventTypeLabels[entry.event_type] || { label: entry.event_type, color: 'text-[#888] bg-[#2a2a2a]' };
                  const delta = entry.qty_delta;
                  const isPositive = delta > 0;
                  const dateStr = new Date(entry.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                  return (
                    <tr key={entry.id} className="border-b border-[#222] hover:bg-[#1e1e1e] transition-colors" data-testid={`history-row-${entry.id}`}>
                      <td className="p-3 text-[#aaa] text-xs whitespace-nowrap">{dateStr}</td>
                      <td className="p-3"><span className="font-mono text-white text-xs">{entry.sku}</span></td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${et.color}`}>{et.label}</span></td>
                      <td className="p-3">
                        <span className={`flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                          {isPositive ? '+' : ''}{delta}
                        </span>
                      </td>
                      <td className="p-3 text-[#888] text-sm">{entry.qty_before}</td>
                      <td className="p-3 text-white text-sm font-medium">{entry.qty_after}</td>
                      <td className="p-3 text-[#888] text-xs max-w-[200px] truncate" title={entry.reason}>{entry.reason || '—'}</td>
                      <td className="p-3 text-[#666] text-xs">{entry.created_by_email || entry.created_by || 'system'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-[#555] text-xs mt-3">Showing {history.length} entries</p>
    </div>
  );
};

// ============== Alerts Tab ==============
const AlertsTab = ({ inventory, getAuthHeaders, onUpdate }) => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/inventory/alerts`, { headers });
      setAlerts(res.data);
    } catch (e) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Alert Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4 text-center">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Total Alerts</p>
              <p className={`text-3xl font-bold ${alerts?.total_alerts > 0 ? 'text-amber-400' : 'text-emerald-400'}`}
                data-testid="total-alerts">{alerts?.total_alerts || 0}</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl border border-red-500/20 p-4 text-center">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Critical (Out of Stock)</p>
              <p className="text-3xl font-bold text-red-400" data-testid="critical-alerts">{alerts?.critical || 0}</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-xl border border-amber-500/20 p-4 text-center">
              <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Warning (Low Stock)</p>
              <p className="text-3xl font-bold text-amber-400" data-testid="warning-alerts">{alerts?.warning || 0}</p>
            </div>
          </div>

          {alerts?.total_alerts === 0 ? (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-8 text-center">
              <Boxes className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
              <p className="text-emerald-400 font-medium text-lg">All stock levels are healthy</p>
              <p className="text-[#888] text-sm mt-1">No items are below their alert thresholds</p>
            </div>
          ) : (
            <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="alerts-table">
                  <thead>
                    <tr className="border-b border-[#2a2a2a]">
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Severity</th>
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">SKU</th>
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Product</th>
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">On Hand</th>
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Threshold</th>
                      <th className="text-left p-4 text-[#888] font-medium text-xs uppercase tracking-wider">Deficit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.alerts.map((alert) => (
                      <tr key={alert.sku} className="border-b border-[#222] hover:bg-[#1e1e1e]" data-testid={`alert-row-${alert.sku}`}>
                        <td className="p-4">
                          {alert.severity === 'critical' ? (
                            <span className="flex items-center gap-1.5 text-xs text-red-400 bg-red-500/15 border border-red-500/20 px-2.5 py-1 rounded-full w-fit">
                              <AlertTriangle className="w-3 h-3" /> Critical
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/15 border border-amber-500/20 px-2.5 py-1 rounded-full w-fit">
                              <AlertTriangle className="w-3 h-3" /> Warning
                            </span>
                          )}
                        </td>
                        <td className="p-4 font-mono text-white text-sm">{alert.sku}</td>
                        <td className="p-4 text-white">{alert.product_name}</td>
                        <td className="p-4 text-lg font-bold text-red-400">{alert.on_hand_qty}</td>
                        <td className="p-4 text-[#888]">{alert.low_stock_threshold}</td>
                        <td className="p-4 text-amber-400 font-medium">{alert.deficit} below</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Threshold Config Info */}
          <div className="mt-6 bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
            <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-[#D4540A]" />
              Configure Thresholds
            </h4>
            <p className="text-[#888] text-sm">
              To adjust alert thresholds per SKU, go to the <strong className="text-white">Overview</strong> tab and click <strong className="text-white">Edit</strong> on any item. 
              The "Alert At" column lets you set when low-stock warnings trigger.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// ============== Main Page ==============
export const AdminInventoryPage = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/inventory`, { headers });
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  return (
    <div className="p-6 lg:p-8" data-testid="admin-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Inventory Management</h1>
          <p className="text-[#888] text-sm">Track stock levels, manage production batches, and configure alerts</p>
        </div>
        <Button variant="outline" onClick={fetchInventory}
          className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a]" data-testid="refresh-inventory-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1a1a1a] border border-[#2a2a2a] p-1 mb-6 w-full sm:w-auto">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4540A] data-[state=active]:text-white text-[#888]"
            data-testid="tab-overview">
            <Boxes className="w-4 h-4 mr-1.5" />Overview
          </TabsTrigger>
          <TabsTrigger value="batch" className="data-[state=active]:bg-[#D4540A] data-[state=active]:text-white text-[#888]"
            data-testid="tab-batch">
            <PackagePlus className="w-4 h-4 mr-1.5" />Add Batch
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-[#D4540A] data-[state=active]:text-white text-[#888]"
            data-testid="tab-history">
            <History className="w-4 h-4 mr-1.5" />History
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-[#D4540A] data-[state=active]:text-white text-[#888]"
            data-testid="tab-alerts">
            <Bell className="w-4 h-4 mr-1.5" />Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab inventory={inventory} loading={loading} onRefresh={fetchInventory} getAuthHeaders={getAuthHeaders} />
        </TabsContent>
        <TabsContent value="batch">
          <AddBatchTab inventory={inventory} getAuthHeaders={getAuthHeaders} onBatchAdded={fetchInventory} />
        </TabsContent>
        <TabsContent value="history">
          <HistoryTab inventory={inventory} getAuthHeaders={getAuthHeaders} />
        </TabsContent>
        <TabsContent value="alerts">
          <AlertsTab inventory={inventory} getAuthHeaders={getAuthHeaders} onUpdate={fetchInventory} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInventoryPage;
